import { Checkbox, Grid, Paper } from "@mui/material";
import { useState, useMemo, Suspense, useCallback, useContext, useEffect } from "react";
import { CustomInput } from "../../../../../shared/CustomInput";
import PlanActions from "../../../../../shared/enrollment/planActions/PlanActions";
import PlanHeader from "../../../../../shared/enrollment/planCode/PlanHeader";
import { AuthContext, GroupOwnerContext, PaycheckContext, ThemeContext } from "../../../../../../contexts";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import { useHistory, useLocation } from "react-router-dom";
import EventNoteIcon from "@mui/icons-material/EventNote";
import {
	dateTimeZoneConverter,
	getCurrentDate,
	manualDateComparator
} from "../../../../../../utils/commonFunctions/date";
import { CommonDetails } from "../../../../../../types/enrollment.types";
import { AddEnrollmentPayload } from "../../../../../../utils/api/enrollment/enrollment";
import { PlanFormProps } from "../../../../../../types/components/enrollmentProps.types";
import { api } from "../../../../../../utils/api";
import { LazyCustomFormDialog } from "../../../../../shared";
import { generateChristmasClubWaiveEnrollmentPayload } from "../../../../../../utils/commonFunctions/groupEnrollment/enrollment";
import { CustomFormDialogType } from "../../../../../../types/components/dialogProps.types";
import { trackPromise } from "react-promise-tracker";

const CceForm = ({ openEnrollment, getEnrollmentInitData }: PlanFormProps): JSX.Element => {
	const { theme } = useContext(ThemeContext);
	const [waiveReason, setWaiveReason] = useState("");
	const [isWaiveEnrollmentTriggered, setIsWaiveEnrollmentTriggered] = useState(false);
	const { member } = useContext(AuthContext);
	const { paycheck } = useContext(PaycheckContext);
	const location = useLocation();
	const history = useHistory();

	const { groupOwner } = useContext(GroupOwnerContext);

	const urlSearchParams: URLSearchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

	const [premiumAmount, setPremiumAmount] = useState<string | null>("0.0");
	const handleWaiveReasonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setWaiveReason(value);
	};

	const [customFormDialogProps, setCustomFormDialogProps] = useState<CustomFormDialogType>();

	const handleWaiveEnrollment = useCallback(async () => {
		if (member === null || openEnrollment === null) {
			return;
		}
		const { standardDetails, commonDetails } = generateChristmasClubWaiveEnrollmentPayload(
			member,
			openEnrollment,
			waiveReason
		);
		const enrollmentPayload: AddEnrollmentPayload = {
			standard_details: standardDetails,
			common_details: { ...commonDetails, agent_id: groupOwner && groupOwner._id ? groupOwner._id : null }
		};
		const response = await trackPromise(api.enrollment.addEnrollment(enrollmentPayload));
		if (response) {
			getEnrollmentInitData(false);
		}
		const step = urlSearchParams.get("step");
		history.push(`?step=${step}&stage=2`);
	}, [getEnrollmentInitData, history, member, openEnrollment, urlSearchParams, waiveReason]);

	const handleWaiveButtonClick = useCallback(() => {
		setCustomFormDialogProps(
			Object.assign({}, customFormDialogProps, {
				openDialog: true,
				title: "Are you sure want to waive this enrollment?",
				textfields: [
					{
						label: "Waive Reason",
						onChange: handleWaiveReasonChange,
						value: waiveReason,
						placeholder: "Enter your reason for waive"
					}
				],
				actions: [
					{
						label: "Cancel",
						callback: () =>
							setCustomFormDialogProps(Object.assign({}, customFormDialogProps, { openDialog: false }))
					},
					{
						label: "Yes",
						callback: () => {
							setCustomFormDialogProps(
								Object.assign({}, customFormDialogProps, {
									openDialog: false
								})
							);
							// handleWaiveEnrollment();
							setIsWaiveEnrollmentTriggered(true);
						}
					}
				]
			})
		);
	}, [customFormDialogProps, handleWaiveEnrollment, waiveReason]);

	const handlePremiumAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		// console.log("value", value);
		if (!/^[0-9 .]*$/.test(value)) {
			// console.log("value insd", value);
			return;
		}
		// console.log("Number(value)", Number(value));
		setPremiumAmount(value);
	};

	const handleActivateEnrollment = useCallback(async () => {
		if (member === null || openEnrollment === null || paycheck === null) {
			return;
		}

		if (paycheck.pay_frequency === "MONTHLY" && Number(premiumAmount) < 13) {
			alert("For an employee with monthly pay frequency premium amount should be greater than $13.0");
			return;
		}

		if (paycheck.pay_frequency === "WEEKLY" && Number(premiumAmount) < 3) {
			alert("For an employee with weekly pay frequency premium amount should be greater than $3.0");
			return;
		}

		const currentDate = new Date() as unknown as string;
		const planStartDate = openEnrollment.plan.start_date; // -1 or 0
		const planDateComparison: number = manualDateComparator(planStartDate, currentDate);

		const effectiveDate = openEnrollment.plan.auto_approve
			? planDateComparison === -1 || planDateComparison === 0
				? getCurrentDate()
				: null
			: null;

		const enrollmentCommonDetails: CommonDetails = {
			agent_id: null,
			location_number: member.location.location_number,
			location_name: member.location.location_name,
			group_number: member.group_number,
			group_name: member.group.name,
			plan_object_id: openEnrollment.plan._id,
			plan_code: openEnrollment.plan.plan_code,
			plan_name: openEnrollment.plan.plan_name,
			base_plan_name: openEnrollment.plan.plan_name,
			insured_object_id: member._id,
			insured_SSN: member.SSN,
			writing_number: 0,
			unenrolled_reason: null,
			waive_reason: null,
			termination_reason: null,
			enrollment_date: getCurrentDate(),
			effective_date: effectiveDate,
			termination_date: null,
			open_enrollment_id: openEnrollment._id,
			total_premium: Number(premiumAmount)
		};
		const enrollmentPayload: AddEnrollmentPayload = {
			common_details: enrollmentCommonDetails,
			standard_details: [
				{
					member_object_id: member._id,
					member_SSN: member.SSN,
					coverage_code: "EE",
					premium_amount: Number(premiumAmount),
					is_tobacco: false
				}
			]
		};
		const response = await trackPromise(api.enrollment.addEnrollment(enrollmentPayload));
		if (response) {
			const step = urlSearchParams.get("step");
			getEnrollmentInitData(false);
			history.push(`?step=${step}&stage=2`);
		}
	}, [getEnrollmentInitData, history, member, openEnrollment, paycheck, premiumAmount, urlSearchParams]);

	useEffect(() => {
		if (isWaiveEnrollmentTriggered) {
			handleWaiveEnrollment();
			setIsWaiveEnrollmentTriggered(false); // Reset the trigger
		}
	}, [isWaiveEnrollmentTriggered, handleWaiveEnrollment]);

	return (
		<div className="theme-container">
			<Suspense fallback={<div />}>
				{customFormDialogProps ? <LazyCustomFormDialog customFormDialogProps={customFormDialogProps} /> : null}
			</Suspense>
			<Paper className="paper-container">
				<div className="header-image-container">
					<PlanHeader
						planCode={openEnrollment?.plan.plan_code}
						planName={openEnrollment?.plan.plan_name as unknown as string}
					/>
				</div>
				<div className="theme-plan-section-margin" />
				<div className="theme-plan-icon-item-container">
					<div>
						<CreateOutlinedIcon className="theme-plan-point-icon" />
					</div>
					<div className="theme-plan-point-text">
						Brookshire Brothers Christmas Club collects all elected deducts for each year from December
						until October.
					</div>
				</div>
				<br></br>
				<div className="theme-plan-icon-item-container">
					<div>
						<CreateOutlinedIcon className="theme-plan-point-icon" />
					</div>
					<div className="theme-plan-point-text">
						This authorization is to withhold the following elected amount for Brookshire Brothers Christmas
						Club from my weekly or monthly payroll check.
					</div>
				</div>
				<br></br>
				<Grid container className="theme-plan-section-margin">
					<Grid item xl={10} lg={10} md={10} sm={10} xs={10}>
						<div className="details-form-row">
							<div className="details-form-label  required">Employee ID</div>
							<CustomInput value={member?.member_id} />
						</div>
					</Grid>
					<Grid item xl={10} lg={10} md={10} sm={10} xs={10}>
						<div className="details-form-row">
							<div className="details-form-label">First Name</div>
							<CustomInput value={member?.first_name} />
						</div>
					</Grid>
					<Grid item xl={10} lg={10} md={10} sm={10} xs={10}>
						<div className="details-form-row">
							<div className="details-form-label">Last Name</div>
							<CustomInput value={member?.last_name} />
						</div>
					</Grid>
					<Grid item xl={10} lg={10} md={10} sm={10} xs={10}>
						<div className="details-form-row">
							<div className="details-form-label">SSN</div>
							<CustomInput value={member?.SSN} />
						</div>
					</Grid>
					<Grid item xl={10} lg={10} md={10} sm={10} xs={10}>
						<div className="details-form-row">
							<div className="details-form-label">Department</div>
							<CustomInput value={member?.location?.location_name} />
						</div>
					</Grid>
				</Grid>
				<div className="details-form-row" style={{ marginTop: "2rem", width: "100%" }}>
					{/* <div className="details-form-label  required">
						The following is the elected weekly amount (Minimum amount is $3.00 per week or $13.00 per
						month).
					</div> */}
					<div className="details-form-label  required">
						The following is the elected amount I wish to be taken out of each paycheck (Minimum amount is
						$3.00 per week or $13.00 per month).
					</div>
					<CustomInput value={premiumAmount} onChange={handlePremiumAmountChange} />
				</div>
				<div className="theme-plan-option-content theme-plan-section-margin">
					<Checkbox defaultChecked style={{ paddingLeft: 0 }} />
					<p className="theme-plan-checkbox-label">
						<strong>
							I understand that this savings plan will be paid out in November through payroll after the
							Christmas Club year is complete or upon my termination. If I terminate at any time during
							the plan year, I understand that I will receive my full contribution amount refunded at the
							earliest possible date after the termination paperwork has been received and processed by
							Human Resources.
						</strong>
					</p>
				</div>
				<div className="theme-plan-section-margin" />
				<div className="theme-plan-icon-item-container">
					<div>
						<EventNoteIcon className="event-icon" style={{ color: theme.primary_color }} />
					</div>
					<div className="theme-plan-text">
						<strong>Note:</strong> All interest earned on the Christmas Club account will be donated to
						Brookshire Brothers Helping Our Own to provide funding to employee owners in need and/or
						educational scholarships to employees or children of employees.
					</div>
				</div>
				<div className="theme-plan-section-margin" />
				<Grid container className="theme-plan-section-margin">
					<Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
						<div className="details-form-row">
							<div className="details-form-label  required">Signature</div>
							<CustomInput value={member?.first_name + " " + member?.last_name} disabled />
						</div>
					</Grid>
					<Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
						<div className="details-form-row">
							<div className="details-form-label  required">Date</div>
							<CustomInput value={dateTimeZoneConverter(getCurrentDate())} disabled />
						</div>
					</Grid>
				</Grid>
				<div className="theme-plan-section-margin" />
				<PlanActions
					waiveButtonCallback={handleWaiveButtonClick}
					activateButtonCallback={handleActivateEnrollment}
				/>
			</Paper>
		</div>
	);
};

export default CceForm;
